import { ReactElement, useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { DRAFT_ALLOCATION_KEY } from '~/services/localStorage/allocations';
import { useStoreActions } from '~/store/hooks';
import Loader from '~/ui/components/common/Loader';
import { NETWORK } from '~/ui/constants/paths';
import { NETWORK_TAB_SLUGS } from '~/ui/pages/Network/constants';

import { IClientAllocationDetailed } from '~/services/api/clientAllocation/types';
import AddAllocationFormComponent from '../AddClientAllocation/components/AllocationForm/AllocationForm';
import { useAddClientAllocationContext } from '../AddClientAllocation/context/AddClientAllocationContext';
import useAddAllocationSubmit from '../AddClientAllocation/hooks/submitAllocations/useAddAllocationSubmit';
import { IAddAllocationFormValues } from '../AddClientAllocation/types';
import useFormStorage from '../hooks/useFormStorage';
import { normalizeDuplicateAllocationsByUser } from '../shared/model/normalizeAllocationsByUser';

interface DuplicateClientAllocationFormProps {
  duplicateAllocation: IClientAllocationDetailed[];
}

const DuplicateClientAllocationForm = ({
  duplicateAllocation,
}: DuplicateClientAllocationFormProps): ReactElement => {
  const { teamMembers, isLoading, actTeamId, locationGroups } = useAddClientAllocationContext();
  const navigate = useNavigate();
  const { showNotify } = useStoreActions(actions => actions.snackbar);

  const defaultValues = useMemo<IAddAllocationFormValues>(
    () => ({
      dateRange: [],
      includeWeekends: false,
      teamMembersAllocations: normalizeDuplicateAllocationsByUser(
        duplicateAllocation,
        teamMembers,
        Number(actTeamId),
        locationGroups,
      ),
    }),
    [teamMembers, duplicateAllocation, actTeamId, locationGroups],
  );

  const form = useForm<IAddAllocationFormValues>({
    values: defaultValues,
    resetOptions: { keepDirtyValues: true },
  });

  const { handleSubmit } = form;

  const { isRestored, clearDraft } = useFormStorage(
    form,
    { key: DRAFT_ALLOCATION_KEY },
    defaultValues,
  );

  const handleSubmitSuccess = useCallback(() => {
    clearDraft();
    navigate(`${NETWORK}?tab=${NETWORK_TAB_SLUGS.MANAGE_ALLOCATION}`);
  }, [clearDraft, navigate]);

  const { submit, isSubmitting } = useAddAllocationSubmit();

  const combinedSubmit = useCallback(
    async (values: IAddAllocationFormValues) => {
      const success = await submit(values);

      if (success) {
        showNotify({ message: 'Client allocation successfully created' });
        handleSubmitSuccess();
      }
    },
    [handleSubmitSuccess, showNotify, submit],
  );

  if (isLoading) {
    return <Loader />;
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(combinedSubmit)}>
        <AddAllocationFormComponent
          isRestored={isRestored}
          isSubmitting={isSubmitting}
          clearDraft={clearDraft}
        />
      </form>
    </FormProvider>
  );
};

export default DuplicateClientAllocationForm;
